/* eslint-disable @typescript-eslint/no-explicit-any */
import { GeneralError, GeneralErrorCause } from '51trips-frontend-core'
import { AxiosError } from 'axios'

// import translations from '../../application/assets/Translations'

const generalErrorConverter = {
  fromFirebaseAuthError: (
    error: any,
    contextDescription: string = 'Firebase Auth error',
    cause?: GeneralErrorCause
  ) => {
    const errorCode = error.code
    if (typeof errorCode === 'string') {
      switch (errorCode) {
        case 'auth/network-request-failed':
          return new GeneralError(contextDescription, GeneralErrorCause.networkConnection, error)
        case 'auth/too-many-requests':
          return new GeneralError(contextDescription, GeneralErrorCause.forbidden, error)
        case 'auth/invalid-email':
          return new GeneralError(contextDescription, GeneralErrorCause.invalidEmail, error)
        case 'auth/email-already-in-use':
          return new GeneralError(contextDescription, GeneralErrorCause.emailAlreadyInUse, error)
        case 'auth/weak-password':
          return new GeneralError(contextDescription, GeneralErrorCause.weakPassword, error)
        case 'auth/wrong-password':
          return new GeneralError(contextDescription, GeneralErrorCause.wrongPassword, error)
        case 'auth/user-not-found':
          return new GeneralError(contextDescription, GeneralErrorCause.userNotFound, error)
        case 'auth/invalid-verification-code':
        case 'auth/invalid-verification-id':
        case 'auth/invalid-action-code':
          return new GeneralError(contextDescription, GeneralErrorCause.invalidCredentials, error)
        case 'auth/code-expired':
        case 'auth/expired-action-code':
          return new GeneralError(contextDescription, GeneralErrorCause.requestTimeout, error)
        case 'auth/invalid-phone-number':
        case 'auth/missing-phone-number':
          return new GeneralError(contextDescription, GeneralErrorCause.invalidData, error)
        default:
          return new GeneralError(contextDescription, cause, error)
      }
    } else {
      return new GeneralError(contextDescription, cause, error)
    }
  },

  fromFirebaseFirestoreError: (
    error: any,
    contextDescription: string = 'Firebase Firestore error',
    cause?: GeneralErrorCause
  ) => {
    const errorCode = error.code
    if (typeof errorCode === 'string') {
      switch (errorCode) {
        case 'firestore/cancelled':
          return new GeneralError(contextDescription, GeneralErrorCause.requestCancelled, error)
        case 'firestore/unknown':
          return new GeneralError(contextDescription, GeneralErrorCause.unknown, error)
        case 'firestore/invalid-argument':
          return new GeneralError(contextDescription, GeneralErrorCause.invalidArgument, error)
        case 'firestore/deadline-exceeded':
          return new GeneralError(contextDescription, GeneralErrorCause.deadlineExceeded, error)
        case 'firestore/not-found':
          return new GeneralError(contextDescription, GeneralErrorCause.dataNotFound, error)
        case 'firestore/already-exists':
          return new GeneralError(contextDescription, GeneralErrorCause.alreadyExists, error)
        case 'firestore/permission-denied':
          return new GeneralError(contextDescription, GeneralErrorCause.permissionDenied, error)
        case 'firestore/resource-exhausted':
          return new GeneralError(contextDescription, GeneralErrorCause.resourceExhausted, error)
        case 'firestore/failed-precondition':
          return new GeneralError(contextDescription, GeneralErrorCause.failedPrecondition, error)
        case 'firestore/aborted':
          return new GeneralError(contextDescription, GeneralErrorCause.aborted, error)
        case 'firestore/out-of-range':
          return new GeneralError(contextDescription, GeneralErrorCause.outOfRange, error)
        case 'firestore/unimplemented':
          return new GeneralError(contextDescription, GeneralErrorCause.unimplemented, error)
        case 'firestore/internal':
          return new GeneralError(contextDescription, GeneralErrorCause.internalServerError, error)
        case 'firestore/unavailable':
          return new GeneralError(contextDescription, GeneralErrorCause.unavailable, error)
        case 'firestore/data-loss':
          return new GeneralError(contextDescription, GeneralErrorCause.dataLoss, error)
        case 'firestore/unauthenticated':
          return new GeneralError(contextDescription, GeneralErrorCause.forbidden, error)
        default:
          return new GeneralError(contextDescription, error)
      }
    } else {
      return new GeneralError(contextDescription, cause, error)
    }
  },

  fromGoogleSignInError: (
    error: any,
    contextDescription: string = 'Firebase Auth error',
    cause?: GeneralErrorCause
  ) => {
    const errorCode = error.code
    if (typeof errorCode === 'string') {
      switch (errorCode) {
        case '12500':
        case '-1':
          return new GeneralError(
            contextDescription,
            GeneralErrorCause.googleLogInNotPossible,
            error
          )
        case '-2':
        case '-6':
          return new GeneralError(contextDescription, undefined, error)
        case '-4':
          return new GeneralError(
            contextDescription,
            GeneralErrorCause.googleInvalidCredential,
            error
          )
        case '12502':
          return new GeneralError(
            contextDescription,
            GeneralErrorCause.googleLogInPendingCompletion,
            error
          )
        case '12501':
        case '-5':
          return new GeneralError(
            contextDescription,
            GeneralErrorCause.googleRequestCancelled,
            error
          )
        case '-7':
          return new GeneralError(
            contextDescription,
            GeneralErrorCause.googleUserNotLoggedIn,
            error
          )
        case '-8':
          return new GeneralError(
            contextDescription,
            GeneralErrorCause.googleUserAlreadyLoggedIn,
            error
          )
        default:
          return new GeneralError(contextDescription, cause, error)
      }
    } else {
      return new GeneralError(contextDescription, cause, error)
    }
  },

  fromAppleSignInError: (
    error: any,
    contextDescription: string = 'Apple Auth error',
    cause?: GeneralErrorCause
  ) => {
    const errorCode = error.code
    if (typeof errorCode === 'string') {
      switch (errorCode) {
        case '1000':
          return new GeneralError(contextDescription, GeneralErrorCause.unknown, error)
        case '1001':
          return new GeneralError(contextDescription, GeneralErrorCause.requestCancelled, error)
        case '1002':
          return new GeneralError(contextDescription, GeneralErrorCause.badRequest, error)
        case '1003':
          return new GeneralError(contextDescription, GeneralErrorCause.unimplemented, error)
        case '1004':
          return new GeneralError(contextDescription, GeneralErrorCause.requestCancelled, error)
        default:
          return new GeneralError(contextDescription, cause, error)
      }
    } else {
      return new GeneralError(contextDescription, cause, error)
    }
  },

  fromGeocodingError: (
    error: any,
    contextDescription: string = 'Geocoding error',
    cause?: GeneralErrorCause
  ) => {
    const errorCode = error.code
    if (typeof errorCode === 'number') {
      switch (errorCode) {
        case 1:
          return new GeneralError(contextDescription, GeneralErrorCause.badRequest, error)
        case 2:
          return new GeneralError(contextDescription, GeneralErrorCause.internalServerError, error)
        case 4:
          return new GeneralError(contextDescription, GeneralErrorCause.dataNotFound, error)
        default:
          return new GeneralError(contextDescription, GeneralErrorCause.unknown, error)
      }
    } else {
      return new GeneralError(contextDescription, cause, error)
    }
  },

  fromStripeCheckoutError: (
    error: any,
    contextDescription: string = 'HTTP request error',
    cause?: GeneralErrorCause
  ) => {
    const formattedErrorCode = error.code

    switch (true) {
      case formattedErrorCode === 400:
        return new GeneralError(contextDescription, GeneralErrorCause.invalidData, error)
      case formattedErrorCode >= 400 && formattedErrorCode < 500:
        return new GeneralError(contextDescription, GeneralErrorCause.invalidData, error)
      case formattedErrorCode >= 500:
        return new GeneralError(contextDescription, GeneralErrorCause.internalServerError, error)
      default:
        return new GeneralError(contextDescription, cause, error)
    }
  },

  fromHttpRequestError: (
    error: any,
    contextDescription: string = 'HTTP request error',
    cause?: GeneralErrorCause
  ) => {
    const errorCode = error.code
    if (typeof errorCode === 'string') {
      const formattedErrorCode = Number(errorCode)

      switch (true) {
        case formattedErrorCode >= 400 && formattedErrorCode < 500:
          return new GeneralError(contextDescription, GeneralErrorCause.invalidData, error)
        case formattedErrorCode >= 500:
          return new GeneralError(contextDescription, GeneralErrorCause.internalServerError, error)
        default:
          return new GeneralError(contextDescription, cause, error)
      }
    } else {
      return new GeneralError(contextDescription, cause, error)
    }
  },

  fromFirebaseStorageError: (
    error: any,
    contextDescription: string = 'Firebase Storage error',
    cause?: GeneralErrorCause
  ) => {
    const errorCode = error.code
    if (typeof errorCode === 'string') {
      switch (errorCode) {
        case 'storage/unknown':
          return new GeneralError(contextDescription, GeneralErrorCause.unknown, error)
        case 'storage/object-not-found':
        case 'storage/bucket-not-found':
        case 'storage/project-not-found':
          return new GeneralError(contextDescription, GeneralErrorCause.dataNotFound, error)
        case 'storage/no-default-bucket':
          return new GeneralError(contextDescription, GeneralErrorCause.internalServerError, error)
        case 'storage/quota-exceeded':
          return new GeneralError(contextDescription, GeneralErrorCause.forbidden, error)
        case 'storage/unauthenticated':
          return new GeneralError(contextDescription, GeneralErrorCause.forbidden, error)
        case 'storage/unauthorized':
          return new GeneralError(contextDescription, GeneralErrorCause.forbidden, error)
        case 'storage/canceled':
          return new GeneralError(contextDescription, GeneralErrorCause.requestCancelled, error)
        case 'storage/retry-limit-exceeded':
        case 'storage/server-file-wrong-size':
        case 'storage/cannot-slice-blob':
        case 'storage/invalid-checksum':
        case 'storage/invalid-event-name':
        case 'storage/invalid-url':
        case 'storage/invalid-argument':
          return new GeneralError(contextDescription, GeneralErrorCause.badRequest, error)
        default:
          return new GeneralError(contextDescription, cause, error)
      }
    } else {
      return new GeneralError(contextDescription, cause, error)
    }
  },

  fromBackendServerError: (error: AxiosError<any>) => {
    let cause

    if (error.code === 'ERR_NETWORK') {
      cause = GeneralErrorCause.networkConnection
    } else if (error.response) {
      switch (error.response.status) {
        case 400:
          cause = GeneralErrorCause.badRequest
          break
        case 401:
          cause = GeneralErrorCause.unauthorized
          break
        case 403:
          cause = GeneralErrorCause.forbidden
          break
        case 404:
          cause = GeneralErrorCause.dataNotFound
          break
        case 409:
          cause = GeneralErrorCause.conflict
          break
        case 500:
          cause = GeneralErrorCause.internalServerError
          break
        default:
          cause = GeneralErrorCause.unknown
          break
      }
    }
    return new GeneralError(error.message, cause || GeneralErrorCause.internalServerError, error)
  }
}

export default generalErrorConverter
